import Papa from "papaparse";
import type { InputPoint } from "./clustering";

const MAX_POINTS = 20_000;

function coordinate(value: unknown): number {
  const parsed = typeof value === "number" ? value : Number(String(value).trim());
  return parsed;
}

function validate(points: InputPoint[]): InputPoint[] {
  const valid = points.filter(
    (item) =>
      Number.isFinite(item.latitude) &&
      Number.isFinite(item.longitude) &&
      item.latitude >= -90 &&
      item.latitude <= 90 &&
      item.longitude >= -180 &&
      item.longitude <= 180,
  );
  if (valid.length === 0) throw new Error("No valid point coordinates were found.");
  if (valid.length > MAX_POINTS) {
    throw new Error(`This browser demo accepts up to ${MAX_POINTS.toLocaleString()} points.`);
  }
  return valid;
}

function fieldValue(row: Record<string, unknown>, names: string[]): unknown {
  const wanted = new Set(names.map((name) => name.toLowerCase()));
  const entry = Object.entries(row).find(([key]) => wanted.has(key.trim().toLowerCase()));
  return entry?.[1];
}

function displayId(value: unknown, fallback: number): string {
  const text = String(value ?? "").trim();
  return text || String(fallback);
}

export async function parseFile(file: File): Promise<InputPoint[]> {
  const text = await file.text();
  if (file.name.toLowerCase().endsWith(".csv")) return parseCsv(text);
  return parseGeoJson(text);
}

export function parseCsv(text: string): InputPoint[] {
  const result = Papa.parse<Record<string, unknown>>(text, {
    header: true,
    skipEmptyLines: true,
    dynamicTyping: true,
  });
  if (result.errors.length && !result.data.length) throw new Error(result.errors[0].message);
  const headers = result.meta.fields ?? [];
  const latitudeKey = headers.find((key) => /^(lat|latitude|y)$/i.test(key.trim()));
  const longitudeKey = headers.find((key) => /^(lon|lng|long|longitude|x)$/i.test(key.trim()));
  if (!latitudeKey || !longitudeKey) {
    throw new Error("CSV requires latitude/lat and longitude/lon/lng columns.");
  }

  return validate(
    result.data.map((row, index) => ({
      id: displayId(fieldValue(row, ["id", "name", "label", "title", "site", "facility", "address"]), index + 1),
      latitude: coordinate(row[latitudeKey]),
      longitude: coordinate(row[longitudeKey]),
      properties: { ...row },
    })),
  );
}

export function parseGeoJson(text: string): InputPoint[] {
  const data = JSON.parse(text) as {
    type?: string;
    features?: Array<{
      id?: string | number;
      geometry?: { type?: string; coordinates?: unknown[] };
      properties?: Record<string, unknown> | null;
    }>;
  };
  if (data.type !== "FeatureCollection" || !Array.isArray(data.features)) {
    throw new Error("GeoJSON must be a FeatureCollection.");
  }

  return validate(
    data.features
      .filter((feature) => feature.geometry?.type === "Point")
      .map((feature, index) => ({
        id: displayId(
          feature.id ??
            fieldValue(feature.properties ?? {}, ["id", "name", "label", "title", "site", "facility", "address"]),
          index + 1,
        ),
        longitude: coordinate(feature.geometry!.coordinates![0]),
        latitude: coordinate(feature.geometry!.coordinates![1]),
        properties: feature.properties ?? {},
      })),
  );
}

function randomGenerator(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

function normal(random: () => number): number {
  const left = Math.max(random(), 1e-12);
  return Math.sqrt(-2 * Math.log(left)) * Math.cos(2 * Math.PI * random());
}

function generateAround(
  centers: Array<[number, number, number]>,
  spread: number,
  seed: number,
): InputPoint[] {
  const random = randomGenerator(seed);
  const result: InputPoint[] = [];
  centers.forEach(([latitude, longitude, count], group) => {
    for (let index = 0; index < count; index++) {
      result.push({
        id: `P-${result.length + 1}`,
        latitude: latitude + normal(random) * spread,
        longitude: longitude + normal(random) * spread * 1.25,
        properties: { syntheticGroup: group + 1 },
      });
    }
  });
  return result;
}

export function demoData(name: string): { label: string; points: InputPoint[]; threshold: number } {
  if (name === "corridor") {
    const random = randomGenerator(29);
    const points: InputPoint[] = Array.from({ length: 320 }, (_, index) => {
      const progress = index / 319;
      return {
        id: `T-${index + 1}`,
        latitude: 43.642 + progress * 0.075 + normal(random) * 0.0015,
        longitude: -79.45 + progress * 0.17 + normal(random) * 0.0015,
        properties: { routePosition: progress },
      };
    });
    return { label: "Toronto transit corridor", points, threshold: 0.4 };
  }
  if (name === "regional") {
    return {
      label: "Great Lakes regional hubs",
      points: generateAround(
        [
          [43.6532, -79.3832, 150],
          [42.3314, -83.0458, 120],
          [41.8781, -87.6298, 140],
          [43.0389, -87.9065, 90],
        ],
        0.045,
        73,
      ),
      threshold: 12,
    };
  }
  return {
    label: "Toronto urban pockets",
    points: generateAround(
      [
        [43.6532, -79.3832, 70],
        [43.6767, -79.3572, 55],
        [43.6426, -79.3871, 60],
        [43.6677, -79.3948, 55],
      ],
      0.003,
      11,
    ),
    threshold: 0.5,
  };
}
